<template>
  <div>
    <div
      :class="containerClasses"
      @click="(isExpanded = !isExpanded)"
      @keydown="(isExpanded = !isExpanded)"
    >
      <div v-if="get(selectedContent, titleKey)">
        <fa
          v-if="get(selectedContent, 'slug')"
          :icon="[isRefresh ? 'fas' : 'fal', getIconName(get(selectedContent, 'slug'))]"
          class="fa-fw mr-1"
          :class="{ 'px-2': isEmbedded }"
        />
        <span
          :class="{
            'group-hover:text-indigo-600': !isRefresh,
            'text-black font-medium text-sm': isRefresh,
          }"
        >
          {{ get(selectedContent, 'name') || getLabel(get(selectedContent, titleKey)) }}
        </span>
      </div>
      <div>
        <fa
          :icon="arrowIcon"
          class="text-lg transition fa-fw rotates"
          :class="{
            'group-hover:text-indigo-600': !isRefresh,
            'text-black font-medium text-sm': isRefresh,
            'fa-rotate-180': isExpanded,
            arrowClass,
          }"
        />
      </div>
    </div>
    <transition
      name="fade"
      :duration="100"
    >
      <div
        v-if="isExpanded"
        class="absolute bg-grimace-50 w-full"
        :class="{
          'mt-4': isEmbedded,
          'shadow-md': !isEmbedded,
        }"
      >
        <div
          v-for="content in contents"
          :key="snakeCase(get(content, titleKey))"
          :class="selectedNavClass(snakeCase(get(content, titleKey)))"
          class="post"
        >
          <vetster-side-nav-link
            :active-nav="activeNav"
            :full-background="true"
            :href="`#${snakeCase(get(content, titleKey))}`"
            :icon="getIconName(get(content, 'slug'))"
            :label="get(content, 'name') || getLabel(get(content, titleKey))"
            :nav-id="snakeCase(get(content, titleKey))"
            :is-embedded="isEmbedded"
            :is-refresh="isRefresh"
            @click="navigateToSection(snakeCase(get(content, titleKey)))"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import get from 'lodash/get';

import VetsterSideNavLink from '@/components/VetsterSideNavLink';
import { snakeCase } from '@/helpers/format';
import HasNavMenu from '@/mixins/HasNavMenu';

const Icons = {
  'new-pet': 'sparkles',
  safety: 'shield',
  travel: 'route',
  gifts: 'gift',
  stress: 'siren-on',
  'key-takeaways': 'key',
  'a-closer-look': 'file-search',
  'connect-with-a-vet': 'user-md',
  'risk-factors': 'exclamation-triangle',
  'possible-causes': 'file-medical',
  references: 'books',
  'testing-and-diagnosis': 'stethoscope',
  'similar-symptoms': 'file-medical',
  'associated-symptoms': 'file-medical',
  'editorial-committee': 'users',
  'more-information': 'info-circle',
  'online-vets': 'user-doctor',
  experience: 'paw',
  dog: 'dog',
  cat: 'cat',
  horse: 'horse',
  turtle: 'turtle',
  bird: 'bird',
  top: 'arrow-up-to-line',
  survey: 'square-poll-vertical',
  articles: 'newspaper',
  next_steps: 'signs-post',
  cities: 'location-dot',
  prescription: 'prescription',
  resources: 'books',
  'meet-our-vets': 'user-doctor',
  'veterinary-care': 'video',
  'what-is-drug': 'prescription',
  'how-it-works': 'file-prescription',
  'how-is-it-given': 'file-prescription',
  'side-effects': 'exclamation-triangle',
  'safety-overdose': 'hand-holding-medical',
  'missed-dosage': 'prescription-bottle-medical',
  'drug-interaction': 'file-medical',
  'alternatives-to-drug': 'prescription-bottle-pill',
  'associated-conditons': 'stethoscope',
  'medications-and-treatments': 'prescription-bottle-pill',
  'conditions-to-manage': 'stethoscope',
  'concerned-about-a-symptom': 'stethoscope',
  'what-can-and-cannot-eat': 'clipboard-question',
  'what-is-the-veterinarian-role': 'user-doctor',
  'how-to-keep-your-companion-healthy': 'hand-holding-medical',
  'dog-breeds': 'dog',
  'symptoms-conditions-behavior': 'stethoscope',
  'history-of-breed': 'book-medical',
  'quick-facts': 'file-magnifying-glass',
  'are-breed-right-for-you': 'clipboard-question',
};

export default {
  name: 'VetsterStickyScroll',

  components: { VetsterSideNavLink },

  mixins: [HasNavMenu],

  props: {
    contents: {
      type: Array,
      default: () => [],
    },
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    isRefresh: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedContent: {},
      isExpanded: false,
      isSticky: false,
      resizeObserver: null,
      scrollByClick: false,
    };
  },

  computed: {
    activeClasses() {
      return this.isRefresh
        ? 'bg-grimace-100 text-black'
        : 'bg-blue-100';
    },
    arrowIcon() {
      return ['fal', 'angle-down'];
    },
    arrowClass() {
      return this.isExpanded && 'fa-rotate-180';
    },
  },

  watch: {
    '$layout.navHeight': {
      handler(value) {
        this.toggleSticky(this.isSticky, value);
      },
    },
  },

  mounted() {
    if (this.contents.length > 0) {
      [this.selectedContent] = this.contents;
    }

    document.addEventListener('scroll', this.scrollHandler);
    document.addEventListener('scroll', this.checkNavPosition);

    this.checkNavPosition();
    this.observeNav();
  },

  beforeDestroy() {
    document.removeEventListener('scroll', this.scrollHandler);
    document.removeEventListener('scroll', this.checkNavPosition);

    this.resizeObserver?.disconnect();
  },

  methods: {
    checkNavPosition() {
      const el = document.querySelector('#contents');

      if (!el) {
        return;
      }

      const buffer = 5;
      const { navHeight } = this.$layout;
      const shouldStick = window.scrollY > el.offsetTop - navHeight - buffer;
      const navHeightChanged = `${navHeight}px` !== el.style.top;

      if (navHeightChanged || (shouldStick !== this.isSticky)) {
        this.toggleSticky(shouldStick);
      }
    },
    getIconName(key) {
      return Icons[key] || '';
    },
    navigateToSection(id) {
      window.scrollTo({
        top: this.$layout.calculateTop(id),
        behavior: 'smooth',
      });
      this.isExpanded = false;
    },
    observeNav() {
      const el = document.querySelector('nav');

      if (el && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.checkNavPosition);
        this.resizeObserver.observe(el);
      }
    },
    scrollHandler() {
      document.querySelectorAll('.post').forEach((el) => {
        const condition = el.getBoundingClientRect().top < 150
          && el.getBoundingClientRect().top >= -el.getBoundingClientRect().height;

        if (condition) {
          this.activeNav = `#${el.id}`;

          const content = this.contents.find(
            (value) => snakeCase(get(value, this.titleKey)) === el.id,
          );
          if (content) {
            this.selectedContent = content;
          }
        }
      });
    },
    selectedNavClass(slug) {
      return this.activeNav === slug ? this.activeClasses : 'bg-white';
    },
    toggleSticky(shouldStick, navHeight = 0) {
      const el = document.querySelector('#contents');

      if (!el) {
        return;
      }

      const navEl = el.firstElementChild;
      const offsetTop = navHeight || this.$layout.navHeight;

      el.classList.toggle('sticky', shouldStick);
      el.classList.toggle('z-30', shouldStick);
      el.style.top = shouldStick ? `${offsetTop}px` : '0';

      navEl.classList.toggle('border-b-ocean-200', shouldStick);
      navEl.classList.toggle('!max-w-full', shouldStick);
      navEl.classList.toggle('xl:rounded-3xl', !shouldStick);
      navEl.classList.toggle('border-ocean-200', !shouldStick);

      this.isSticky = shouldStick;
    },
    get,
    snakeCase,
  },
};
</script>
